import { useEffect, useState } from "react";
import { motion } from "framer-motion";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "pinlayr_gate_token";
const ROLE_KEY = "pinlayr_gate_role";

export default function PasswordGate({ children }) {
  // 'checking' → verifying stored token; 'locked' → show gate; 'unlocked' → render children
  const [state, setState] = useState("checking");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // On mount, verify any previously stored token
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setState("locked");
      return;
    }
    fetch(`${API}/api/gate/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.valid) {
          if (d.role) localStorage.setItem(ROLE_KEY, d.role);
          setState("unlocked");
        } else {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(ROLE_KEY);
          setState("locked");
        }
      })
      .catch(() => {
        // If the backend is unreachable, fail closed — show gate rather than exposing content
        setState("locked");
      });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/gate/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        setError("That password isn’t right. Please try again.");
        setSubmitting(false);
        return;
      }
      const { token, role } = await res.json();
      localStorage.setItem(TOKEN_KEY, token);
      if (role) localStorage.setItem(ROLE_KEY, role);
      setState("unlocked");
    } catch (err) {
      setError("Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  if (state === "unlocked") return children;

  // While checking a stored token, render a blank screen so no protected
  // content flashes before verification completes.
  if (state === "checking") {
    return (
      <div
        className="fixed inset-0 z-[9999] bg-slate-900"
        data-testid="password-gate-checking"
      />
    );
  }

  return (
    <div
      className="fixed inset-0 z-[9999] bg-slate-900 text-white flex items-center justify-center px-6 overflow-hidden"
      data-testid="password-gate"
    >
      {/* Ambient glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(60% 55% at 50% 40%, rgba(99,102,241,0.18) 0%, rgba(15,23,42,0) 60%)",
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-[420px] text-center"
      >
        {/* Logo mark */}
        <div className="mx-auto mb-10 flex items-center justify-center gap-2.5">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-indigo-600 text-lg font-bold text-white">
            P
          </span>
          <span className="text-xl font-semibold tracking-tight">Pinlayr</span>
        </div>

        <div
          className="font-mono text-[11px] tracking-[0.28em] uppercase text-white/50 mb-4"
          data-testid="gate-eyebrow"
        >
          Preview access
        </div>
        <h1
          className="text-[26px] sm:text-[30px] leading-[1.15] tracking-tight font-semibold mb-10"
          data-testid="gate-headline"
        >
          Pinlayr · Commenting Suite
        </h1>

        <form onSubmit={submit} className="space-y-4" data-testid="gate-form">
          <div className="relative">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoFocus
              autoComplete="current-password"
              disabled={submitting}
              data-testid="gate-password-input"
              className="w-full bg-white/[0.06] border border-white/20 focus:border-indigo-400 focus:bg-white/[0.09] outline-none rounded-lg px-4 py-3.5 text-[15px] text-white placeholder-white/40 transition-colors disabled:opacity-60"
            />
          </div>

          {error && (
            <div
              className="text-[12px] text-red-300 font-mono tracking-wide"
              data-testid="gate-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !password}
            data-testid="gate-submit"
            className="w-full bg-indigo-600 text-white py-3.5 font-medium text-[14px] tracking-[0.12em] uppercase rounded-lg hover:bg-indigo-500 active:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Unlocking…" : "Enter"}
          </button>
        </form>

        <div className="mt-10 font-mono text-[10px] tracking-[0.24em] uppercase text-white/40">
          Pinlayr · Commenting Suite
        </div>
      </motion.div>
    </div>
  );
}
